import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from './cx';
import Icon from './Icon';

export const scrollbarVariants = ['default', 'dark'] as const;
export type ScrollbarVariant = (typeof scrollbarVariants)[number];

interface ScrollbarProps {
  variant?: ScrollbarVariant;
  className?: string;
  children: React.ReactNode;
}

interface Thumb {
  top: number;
  height: number;
}

const Scrollbar: React.FC<ScrollbarProps> = ({
  variant = 'default',
  className,
  children,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<Thumb>({ top: 0, height: 24 });

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return;
    const trackHeight = track.clientHeight || 1;
    const visible = scroller.clientHeight;
    const total = scroller.scrollHeight;
    const ratio = total <= 0 ? 1 : visible / total;
    const height = Math.max(16, Math.min(trackHeight, ratio * trackHeight));
    const maxScroll = total - visible;
    const travel = Math.max(0, trackHeight - height);
    const top = maxScroll <= 0 ? 0 : (scroller.scrollTop / maxScroll) * travel;
    setThumb({ top, height });
  }, []);

  useEffect(() => {
    measure();
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    scroller.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    return () => {
      scroller.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure, children]);

  const scrollByPixels = (delta: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTop += delta;
    measure();
  };

  const onThumbDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return;
    const startY = event.clientY;
    const startTop = scroller.scrollTop;
    const maxScroll = scroller.scrollHeight - scroller.clientHeight;
    const travel = Math.max(1, track.clientHeight - thumb.height);

    const move = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientY - startY;
      scroller.scrollTop = startTop + (delta / travel) * maxScroll;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      className={cx(
        'osrs-scroll-frame',
        variant === 'dark' && 'osrs-scroll-frame--dark',
        className
      )}
    >
      <div className="osrs-scroll" ref={scrollerRef}>
        {children}
      </div>
      <div className="osrs-scroll-bar">
        <button type="button" className="osrs-btn" aria-label="Scroll up" onClick={() => scrollByPixels(-24)}>
          <Icon name="arrow-up" />
        </button>
        <div className="osrs-scroll-track" ref={trackRef}>
          <div
            className="osrs-scroll-thumb"
            style={{ top: thumb.top, height: thumb.height }}
            onPointerDown={onThumbDown}
          />
        </div>
        <button type="button" className="osrs-btn" aria-label="Scroll down" onClick={() => scrollByPixels(24)}>
          <Icon name="arrow-down" />
        </button>
      </div>
    </div>
  );
};

export default Scrollbar;
